// "Log something earlier": turning "a nap, started about 45 min ago, lasted
// an hour" or "a wee at 14:10" into an entry — for the things nobody tapped
// at the time.

import { DAY, MINUTE, dayStart, formatDate, formatTime } from './time.ts';
import type { EventDataMap, ISODate, LogEvent, Millis } from './types.ts';

export type EarlierKind = 'nap' | 'wee' | 'poo' | 'accident' | 'meal' | 'play' | 'walk' | 'training';

/** Things with a start and an end. */
export const TIMED: EarlierKind[] = ['nap', 'play', 'walk'];

/** "Started 45 min ago", or "at 13:10" — on a given day, or else today (yesterday if that's still to come). */
export type When = { ago: number } | { clock: string; date?: ISODate };

/** How long it lasted, in minutes; or the clock time it ended ("woke at 14:40"); or still going (still asleep). */
export type Lasted = number | 'ongoing' | { until: string };

/** "Woke at 06:15": the first time on that clock after the start (so an overnight sleep ends the next morning). */
export function resolveUntil(clock: string, at: Millis): Millis {
  const [h, m] = clock.split(':').map(Number);
  const d = new Date(at);
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m).getTime();
  return end <= at ? end + DAY : end;
}

/** The end time a Lasted gives, if it has one. */
export function lastedEnd(lasted: Lasted | undefined, at: Millis): Millis | undefined {
  if (typeof lasted === 'number') return at + lasted * MINUTE;
  if (lasted && typeof lasted === 'object') return resolveUntil(lasted.until, at);
  return undefined;
}

export function resolveWhen(w: When, now: Millis): Millis {
  if ('ago' in w) return now - w.ago * MINUTE;
  const [h, m] = w.clock.split(':').map(Number);
  if (w.date) {
    const [y, mo, d] = w.date.split('-').map(Number);
    return new Date(y, mo - 1, d, h, m).getTime();
  }
  const d = new Date(now);
  const at = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m).getTime();
  return at > now + MINUTE ? at - DAY : at;
}

export interface EarlierEntry<T extends keyof EventDataMap = keyof EventDataMap> {
  type: T;
  data: EventDataMap[T];
  at: Millis;
  end?: Millis;
}

/** The entry to log. A finish in the future is cut back to now. */
export function earlierEntry(
  kind: EarlierKind,
  at: Millis,
  opts: { lasted?: Lasted; accident?: 'wee' | 'poo'; grams?: number; now: Millis },
): EarlierEntry {
  const finish = TIMED.includes(kind) ? lastedEnd(opts.lasted, at) : undefined;
  const end = finish !== undefined ? Math.min(opts.now, finish) : undefined;
  const timed = (type: 'sleep' | 'play' | 'walk'): EarlierEntry => ({ type, data: {}, at, ...(end !== undefined ? { end: Math.max(end, at + MINUTE) } : {}) });
  switch (kind) {
    case 'nap':
      return timed('sleep');
    case 'play':
      return timed('play');
    case 'walk':
      return timed('walk');
    case 'wee':
      return { type: 'wee', data: { where: 'outside' }, at };
    case 'poo':
      return { type: 'poo', data: { where: 'outside' }, at };
    case 'accident':
      return { type: opts.accident ?? 'wee', data: { where: 'inside' }, at };
    case 'meal':
      return { type: 'meal', data: { appetite: 'all', ...(opts.grams ? { grams: opts.grams } : {}) }, at };
    case 'training':
      return { type: 'training', data: { skills: [] }, at };
  }
}

/** A logged event of the same kind that this one would overlap (or sit within 10 minutes of, for one-off things). */
export function clash(entry: EarlierEntry, events: LogEvent[], now: Millis): LogEvent | undefined {
  const end = entry.end ?? (entry.type === 'sleep' || entry.type === 'walk' || entry.type === 'play' ? now : undefined);
  return events.find((e) => {
    if (e.type !== entry.type) return false;
    if (end === undefined) return Math.abs(e.at - entry.at) < 10 * MINUTE;
    const eEnd = e.end ?? now;
    return e.at < end && entry.at < eEnd;
  });
}

/** How a day reads: "" for today, "yesterday ", "Mon 26 Oct ". */
export function dayLabel(t: Millis, now: Millis): string {
  const back = Math.round((dayStart(now) - dayStart(t)) / DAY);
  return back <= 0 ? '' : back === 1 ? 'yesterday ' : `${formatDate(t, 'weekday')} `;
}

/** "13:10–14:40", "13:10 – still asleep", "14:10", "yesterday 22:30–06:15". */
export function describeSpan(entry: EarlierEntry, now: Millis): string {
  const day = dayLabel(entry.at, now);
  if (entry.type !== 'sleep' && entry.type !== 'play' && entry.type !== 'walk') return `${day}${formatTime(entry.at)}`;
  if (entry.end === undefined) return `${day}${formatTime(entry.at)} – ${entry.type === 'sleep' ? 'still asleep' : 'still going'}`;
  return `${day}${formatTime(entry.at)}–${formatTime(entry.end)}`;
}
